import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { FluentProvider, Subtitle1, makeStyles, shorthands, tokens } from '@fluentui/react-components';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import Chat from './components/chat/Chat';
import ghsLogo from './ghs-logo-circle.svg';
import { Loading, Login } from './components/views';
import { AuthHelper } from './libs/auth/AuthHelper';
import { useChat, useFile } from './libs/hooks';
import { AlertType } from './libs/models/AlertType';
import { useAppDispatch, useAppSelector } from './redux/app/hooks';
import { RootState } from './redux/app/store';
import { FeatureKeys } from './redux/features/app/AppState';
import { addAlert, setActiveUserInfo, setServiceInfo } from './redux/features/app/appSlice';
import { genesisHealthcareDarkTheme, genesisHealthcareLightTheme } from './styles';
import { PolicyAgreementDialog } from './components/policy/PolicyAgreementDialog';
import { UserPolicyAgreementService } from './libs/services/UserPolicyAgreementService';

export const useClasses = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        width: '100%',
        ...shorthands.overflow('hidden'),
    },
    header: {
        alignItems: 'center',
        backgroundColor: '#00583f', // Genesis Green
        color: tokens.colorNeutralForegroundOnBrand,
        display: 'flex',
        '& h1': {
            paddingLeft: tokens.spacingHorizontalXL,
            display: 'flex',
        },
        height: '48px',
        justifyContent: 'space-between',
        width: '100%',
    },
    persona: {
        marginRight: tokens.spacingHorizontalXXL,
    },
    cornerItems: {
        display: 'flex',
        ...shorthands.gap(tokens.spacingHorizontalS),
    },
    logoDiv: {
        display: 'flex',
        alignItems: 'center',
    },
    logo: {
        height: '32px',
        width: '32px',
        display: 'inline-block',
        paddingLeft: '12px',
    },
});

export enum AppState {
    ProbeForBackend,
    SettingUserInfo,
    CheckingPolicyAgreement,
    ErrorLoadingChats,
    ErrorLoadingUserInfo,
    LoadChats,
    LoadingChats,
    Chat,
    SigningOut,
}

const App = () => {
    const classes = useClasses();
    const [appState, setAppState] = React.useState(AppState.ProbeForBackend);
    const [showPolicyDialog, setShowPolicyDialog] = useState(false);
    const dispatch = useAppDispatch();
    const { instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const { features, isMaintenance } = useAppSelector((state: RootState) => state.app);

    const chat = useChat();
    const file = useFile();
    
    // Create service instance once using useMemo to avoid recreating on every render
    const policyService = React.useMemo(() => new UserPolicyAgreementService(), []);

    const handleAppStateChange = useCallback((newState: AppState) => {
        setAppState(newState);
    }, []);

    const handlePolicyAgree = useCallback(async () => {
        try {
            const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
            await policyService.createPolicyAgreementAsync(accessToken);
            setShowPolicyDialog(false);
            handleAppStateChange(AppState.LoadChats);
        } catch (error) {
            console.error('Error creating policy agreement:', error);
            dispatch(
                addAlert({
                    message: 'Failed to save policy agreement. Please try again.',
                    type: AlertType.Error,
                }),
            );
        }
    }, [instance, inProgress, policyService, handleAppStateChange, dispatch]);

    useEffect(() => {
        if (isMaintenance && appState !== AppState.ProbeForBackend) {
            handleAppStateChange(AppState.ProbeForBackend);
            return;
        }

        if (isAuthenticated && appState === AppState.SettingUserInfo) {
            const account = instance.getActiveAccount();
            if (!account) {
                handleAppStateChange(AppState.ErrorLoadingUserInfo);
            } else {
                dispatch(
                    setActiveUserInfo({
                        id: `${account.localAccountId}.${account.tenantId}`,
                        email: account.username,
                        username: account.name ?? account.username,
                    }),
                );

                if (account.username.split('@')[1] === 'microsoft.com') {
                    dispatch(
                        addAlert({
                            message:
                                'By using GenGPT, you agree to protect sensitive data, not store it in chat, and allow chat history collection for service improvements. This tool is for internal use only.',
                            type: AlertType.Info,
                        }),
                    );
                }

                handleAppStateChange(AppState.CheckingPolicyAgreement);
            }
        }

        if (isAuthenticated && appState === AppState.CheckingPolicyAgreement) {
            void (async () => {
                try {
                    const accessToken = await AuthHelper.getSKaaSAccessToken(instance, inProgress);
                    const agreement = await policyService.getPolicyAgreementAsync(accessToken);
                    
                    if (agreement?.agreed) {
                        // User has already agreed to policies
                        handleAppStateChange(AppState.LoadChats);
                    } else {
                        // User needs to agree to policies
                        setShowPolicyDialog(true);
                    }
                } catch (error) {
                    console.error('Error checking policy agreement:', error);
                    // Show dialog if we can't check - safer to require agreement
                    setShowPolicyDialog(true);
                }
            })();
        }

        if ((isAuthenticated || !AuthHelper.isAuthAAD()) && appState === AppState.LoadChats) {
            handleAppStateChange(AppState.LoadingChats);
            void Promise.all([
                chat
                    .loadChats()
                    .then(() => {
                        handleAppStateChange(AppState.Chat);
                    })
                    .catch((error) => {
                        console.error('Error loading chats:', error);
                        handleAppStateChange(AppState.ErrorLoadingChats);
                    }),
                file.getContentSafetyStatus(),
                chat.getServiceInfo().then((serviceInfo) => {
                    if (serviceInfo) {
                        dispatch(setServiceInfo(serviceInfo));
                    }
                }),
            ]);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance, isAuthenticated, appState, isMaintenance]);

    const theme = features[FeatureKeys.DarkMode].enabled ? genesisHealthcareDarkTheme : genesisHealthcareLightTheme;

    return (
        <FluentProvider className="app-container" theme={theme}>
            {showPolicyDialog && <PolicyAgreementDialog onAgree={() => void handlePolicyAgree()} />}
            {AuthHelper.isAuthAAD() ? (
                <>
                    <UnauthenticatedTemplate>
                        <div className={classes.container}>
                            <div className={classes.header} aria-label="Application Header">
                                <span className={classes.logoDiv}>
                                    <img src={ghsLogo} className={classes.logo} alt="GHS Logo" />
                                    <Subtitle1 as="h1">GenGPT</Subtitle1>
                                </span>
                            </div>
                            {appState === AppState.SigningOut && <Loading text="Signing you out..." />}
                            {appState !== AppState.SigningOut && <Login />}
                        </div>
                    </UnauthenticatedTemplate>
                    <AuthenticatedTemplate>
                        <Chat classes={classes} appState={appState} setAppState={handleAppStateChange} />
                    </AuthenticatedTemplate>
                </>
            ) : (
                <Chat classes={classes} appState={appState} setAppState={handleAppStateChange} />
            )}
        </FluentProvider>
    );
};

export default App;
